import { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { testimonials } from '../data/mock';
import StarRating from '../components/StarRating';
import ReviewForm from '../components/ReviewForm';
import { Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const Reviews = () => {
  const [allReviews, setAllReviews] = useState(testimonials);
  const [filterRating, setFilterRating] = useState('all');

  const handleNewReview = (review) => {
    setAllReviews(prev => [review, ...prev]);
  };

  const filteredReviews = filterRating === 'all' 
    ? allReviews 
    : allReviews.filter(review => review.rating === parseInt(filterRating));

  const averageRating = allReviews.length > 0
    ? (allReviews.reduce((sum, review) => sum + review.rating, 0) / allReviews.length).toFixed(1)
    : 0;

  const ratingCounts = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: allReviews.filter(r => r.rating === rating).length,
    percentage: allReviews.length > 0 
      ? Math.round((allReviews.filter(r => r.rating === rating).length / allReviews.length) * 100)
      : 0
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section 
        className="relative h-[300px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1544945582-052b29cd29e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMHBhcmFkaXNlfGVufDB8fHx8MTc3NDM5ODgyN3ww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Customer Reviews</h1>
          <p className="text-xl">See what our customers are saying about us</p>
        </div>
      </section>

      {/* Reviews Stats */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <Card className="border-red-600/30">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Overall Rating */}
                  <div className="text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-4 mb-4">
                      <div className="text-6xl font-bold text-red-600">{averageRating}</div>
                      <div>
                        <StarRating rating={Math.round(parseFloat(averageRating))} size="large" />
                        <p className="text-gray-600 mt-1">{allReviews.length} {allReviews.length === 1 ? 'Review' : 'Reviews'}</p>
                      </div>
                    </div>
                    <p className="text-gray-700">Based on customer feedback</p>
                  </div>

                  {/* Rating Breakdown */}
                  <div className="space-y-2">
                    {ratingCounts.map(({ rating, count, percentage }) => (
                      <div key={rating} className="flex items-center gap-3">
                        <span className="text-sm font-medium w-12">{rating} stars</span>
                        <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-600 transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Reviews List */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Filter */}
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold">All Reviews</h2>
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-600" />
                <Select value={filterRating} onValueChange={setFilterRating}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Filter by rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ratings</SelectItem>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4 Stars</SelectItem>
                    <SelectItem value="3">3 Stars</SelectItem>
                    <SelectItem value="2">2 Stars</SelectItem>
                    <SelectItem value="1">1 Star</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Reviews Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {filteredReviews.map((review) => (
                <Card key={review.id} className="hover:shadow-xl transition-shadow">
                  <CardContent className="pt-6">
                    <StarRating rating={review.rating} size="default" />
                    <p className="text-gray-700 my-4 italic line-clamp-4">"{review.text}"</p>
                    <div className="border-t pt-4">
                      <p className="font-semibold">{review.name}</p>
                      <p className="text-sm text-gray-600">{review.location}</p>
                      <p className="text-xs text-gray-500 mt-1">{new Date(review.date).toLocaleDateString()}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredReviews.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No reviews found for this rating.</p>
              </div>
            )}

            {/* Review Form */}
            <div className="mt-16">
              <h2 className="text-3xl font-bold text-center mb-8">Write a Review</h2>
              <div className="max-w-3xl mx-auto">
                <ReviewForm onReviewSubmit={handleNewReview} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
